import { apiGet, apiPatch, apiDel } from '../api.js';
import { token as getToken } from '../auth.js';
import { t } from '../i18n.js';
import { toast } from '../components/toast.js';
import { pendingBadgeFor, applyOptimistic, reconcile } from './outbox-pending.js';
import { timerCreateForm } from '../components/timer-create-form.js';
import { timerCard } from '../components/timer-card.js';
import { unlockTimerAudio, timerAlarmBanner } from '../components/timer-alarm.js';

import { stateEmpty, stateError, stateLoading } from '../components/state-card.js';

// ---------------------------------------------------------------------------
// Poll interval (ms) — ~25 s; paused while document is hidden. Mirrors
// pages/shopping.js exactly (see spec "Cross-device sync via polling").
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 25000;
const TICK_INTERVAL_MS = 1000;

// ---------------------------------------------------------------------------
// Pure countdown helpers — exported so they can be unit-tested directly
// without mounting the page. The server is the single source of truth for
// `ends_at` (design D1); these helpers never trust a server-sent remaining
// value for a running timer.
// ---------------------------------------------------------------------------

/**
 * Milliseconds between now and a timer's authoritative `ends_at`. Negative
 * once elapsed. Returns null when `endsAtIso` is missing/unparseable.
 *
 * @param {string|null} endsAtIso
 * @param {number} nowMs
 * @returns {number|null}
 */
export function remainingMsFromEndsAt(endsAtIso, nowMs = Date.now()) {
  if (!endsAtIso) return null;
  const endsAtMs = new Date(endsAtIso).getTime();
  if (Number.isNaN(endsAtMs)) return null;
  return endsAtMs - nowMs;
}

/**
 * The value to display for a timer at the given instant: derived from
 * `ends_at` while running, from the stored `remaining_ms` while paused, and
 * 0 otherwise. Never negative.
 *
 * @param {{status: string, ends_at: string|null, remaining_ms: number|null}} timer
 * @param {number} nowMs
 * @returns {number}
 */
export function deriveDisplayMs(timer, nowMs = Date.now()) {
  if (timer.status === 'running') {
    const rem = remainingMsFromEndsAt(timer.ends_at, nowMs);
    return rem === null ? 0 : Math.max(0, rem);
  }
  if (timer.status === 'paused') {
    return Math.max(0, timer.remaining_ms ?? 0);
  }
  return 0;
}

/**
 * Summarise the running-timer state for the shared header indicator
 * (components/timer-indicator.js): how many timers are currently `running`
 * and the soonest one's remaining time. Only `running` timers count — paused
 * timers have no `ends_at` and are not "on the clock" from the household's
 * point of view.
 *
 * @param {Array<{status: string, ends_at: string|null}>} timers
 * @param {number} nowMs
 * @returns {{count: number, soonestMs: number|null}} `soonestMs` is null iff `count === 0`.
 */
export function runningTimerSummary(timers, nowMs = Date.now()) {
  const running = timers.filter((timer) => timer.status === 'running');
  if (running.length === 0) return { count: 0, soonestMs: null };

  let soonestMs = null;
  for (const timer of running) {
    const rem = remainingMsFromEndsAt(timer.ends_at, nowMs);
    const clamped = rem === null ? 0 : Math.max(0, rem);
    if (soonestMs === null || clamped < soonestMs) soonestMs = clamped;
  }
  return { count: running.length, soonestMs };
}

/**
 * True when a `running` timer's `ends_at` has been reached or passed.
 *
 * @param {{status: string, ends_at: string|null}} timer
 * @param {number} nowMs
 * @returns {boolean}
 */
export function hasElapsed(timer, nowMs = Date.now()) {
  if (timer.status !== 'running') return false;
  const rem = remainingMsFromEndsAt(timer.ends_at, nowMs);
  return rem !== null && rem <= 0;
}

/**
 * All timers in the list whose `ends_at` has been reached at `nowMs`.
 *
 * @param {Array<object>} timers
 * @param {number} nowMs
 * @returns {Array<object>}
 */
export function findElapsedTimers(timers, nowMs = Date.now()) {
  return timers.filter((timer) => hasElapsed(timer, nowMs));
}

/**
 * Format milliseconds as `mm:ss`, or `h:mm:ss` once past an hour. Rounds up
 * (not to nearest) so the display only reaches 00:00 once the timer has
 * actually elapsed, never a moment early.
 *
 * @param {number} ms
 * @returns {string}
 */
export function formatDuration(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/**
 * Replace a timer by id in a list, appending it when not already present.
 * Pure — returns a new array, exported for tests.
 *
 * @param {Array<object>} timers
 * @param {object} updated
 * @returns {Array<object>}
 */
export function applyTimerUpdate(timers, updated) {
  const idx = timers.findIndex((timer) => timer.id === updated.id);
  if (idx === -1) return [...timers, updated];
  const copy = timers.slice();
  copy[idx] = updated;
  return copy;
}

/**
 * Remove a timer by id from a list. Pure — returns a new array.
 *
 * @param {Array<object>} timers
 * @param {string} id
 * @returns {Array<object>}
 */
export function removeTimerById(timers, id) {
  return timers.filter((timer) => timer.id !== id);
}

// ---------------------------------------------------------------------------
// Foreground alarm seam — defaults to the real components/timer-alarm.js
// implementation (repeating Web Audio tone + flashing/steady
// (prefers-reduced-motion-aware) optical banner). `alarmSeam.fn` is called as
// `(timer, alarmSurfaceEl, onDismiss)`; tests may override it with a fake to
// assert wiring without exercising Web Audio/animation directly.
// ---------------------------------------------------------------------------

export const alarmSeam = { fn: timerAlarmBanner };

// ---------------------------------------------------------------------------
// Elapse-PATCH seam — fires a background, best-effort PATCH
// `{ action: 'elapsed' }` (idempotent) so the worker's `listDuePushable`
// excludes the timer and skips the redundant Web Push (design D6 / spec
// "Client marks elapsed and suppresses redundant push"). Guarded so at most
// one PATCH is sent per timer id per elapse episode — the guard lives here
// (module scope) rather than relying solely on the caller's `alarmedIds`
// check, so the seam stays correct and unit-testable in isolation. Never
// gates the foreground alarm: it is invoked fire-and-forget (not awaited) and
// swallows all errors (offline, already-elapsed-server-side, network) — the
// PATCH is a background suppression signal, not a correctness requirement.
// `forgetElapsePatch` lets the guard reset when a timer restarts and elapses
// again (mirrors the `alarmedIds`/`pruneAlarmedIds` reset below).
// ---------------------------------------------------------------------------

const elapsePatchedIds = new Set();

/**
 * @param {{id: string}} timer
 */
export function sendElapsePatch(timer) {
  if (elapsePatchedIds.has(timer.id)) return;
  elapsePatchedIds.add(timer.id);
  apiPatch(`/timers/${timer.id}`, { action: 'elapsed' }).catch(() => {
    // Best-effort suppression signal only; the alarm already fired locally.
  });
}

/** @param {string} id */
export function forgetElapsePatch(id) {
  elapsePatchedIds.delete(id);
}

export const elapseSeam = { fn: sendElapsePatch };

// ---------------------------------------------------------------------------
// Outcome copy (IG-R5) — one whole key per action, the past tense of the
// button that was pressed. Never composed at runtime from fragments, and never
// the button's own label read back ("Pause" → "Timer paused.").
// ---------------------------------------------------------------------------

export const CONTROL_TOAST_KEY = {
  pause: 'timers.toast_paused',
  resume: 'timers.toast_resumed',
  add_minute: 'timers.toast_minute_added',
  restart: 'timers.toast_restarted',
};

// ---------------------------------------------------------------------------
// Page mount
// ---------------------------------------------------------------------------

export const route = {
  async mount(target) {
    const state = { timers: [], loadError: false };
    const cardRefs = new Map(); // timer id -> { remainingEl }
    const alarmedIds = new Set();
    const activeAlarms = new Map(); // timer id -> { el, stop } handle from alarmSeam.fn

    // Offline mutations queued by the service-worker outbox (IG-R10). Timers
    // are outbox-allowlisted in api.js for POST/PATCH/DELETE, so all three verbs
    // can come back as a `{queued:true,key}` envelope instead of an entity.
    // Structure: Map<outboxKey, { kind: 'create'|'control'|'cancel',
    //                             id: string|null, snapshot: object|null }>
    const pendingOutbox = new Map();

    const main = document.createElement('main');
    main.className = 'space-y-6 pb-nav-safe';

    const heading = document.createElement('h1');
    heading.textContent = t('timers.title');
    main.append(heading);

    const createFormMount = document.createElement('div');
    createFormMount.dataset.role = 'timer-create-form-mount';
    main.append(createFormMount);

    // Pending-sync surface for queued mutations that have no row to hang a
    // badge on (a create has no id yet; a cancel removed its row). Queued
    // control actions badge their own card instead — see renderPending.
    const pendingWrap = document.createElement('div');
    pendingWrap.className = 'flex flex-wrap gap-2';
    pendingWrap.dataset.role = 'timers-pending';
    main.append(pendingWrap);

    // Fired-alarm surface — see alarmSeam comment above.
    const alarmSurfaceEl = document.createElement('div');
    alarmSurfaceEl.className = 'space-y-2';
    alarmSurfaceEl.dataset.role = 'timer-alarm-surface';
    main.append(alarmSurfaceEl);

    const listWrap = document.createElement('div');
    listWrap.className = 'space-y-3';
    listWrap.dataset.role = 'timer-list';
    main.append(listWrap);

    // Unlock the shared AudioContext on the first user gesture anywhere on
    // this page (creating a timer or tapping any control both count) — design
    // D7. Must run synchronously inside the click handler to satisfy browser
    // autoplay policy; safe to attempt repeatedly, so { once: true } is purely
    // an optimisation, not a correctness requirement.
    main.addEventListener('click', unlockTimerAudio, { once: true });

    target.append(main);

    // -------------------------------------------------------------------
    // stopAlarm — stops the tone, removes the banner, and forgets the handle
    // for a single timer id. Idempotent (safe to call more than once, e.g.
    // once from the banner's own Dismiss button and again from the
    // handleCancel it triggers) because both `stop()` and `el.remove()` are
    // themselves idempotent. This is the single choke point that prevents the
    // runaway-alarm leak: every path that stops ringing a timer — dismiss,
    // cancel, prune, or page cleanup — routes through here.
    // -------------------------------------------------------------------

    function stopAlarm(id) {
      const handle = activeAlarms.get(id);
      if (!handle) return;
      activeAlarms.delete(id);
      if (typeof handle.stop === 'function') handle.stop();
      if (handle.el && typeof handle.el.remove === 'function') handle.el.remove();
    }

    // -------------------------------------------------------------------
    // pruneAlarmedIds — drops ids whose server-reported state no longer
    // satisfies hasElapsed (e.g. once the elapse PATCH lands and sets status
    // to 'elapsed', or the timer was restarted/cancelled), so a fresh elapse
    // can fire again. Also stops any still-ringing tone/banner for a pruned
    // id — otherwise a timer control action or poll refresh could silently
    // leave a tone beeping with no way to dismiss it.
    // -------------------------------------------------------------------

    function pruneAlarmedIds() {
      const now = Date.now();
      for (const id of Array.from(alarmedIds)) {
        const timer = state.timers.find((t2) => t2.id === id);
        if (!timer || !hasElapsed(timer, now)) {
          alarmedIds.delete(id);
          forgetElapsePatch(id);
          stopAlarm(id);
        }
      }
    }

    function onElapseDetected(timer) {
      if (alarmedIds.has(timer.id)) return;
      alarmedIds.add(timer.id);

      const fireAlarm = alarmSeam.fn ?? timerAlarmBanner;
      const handle = fireAlarm(timer, alarmSurfaceEl, () => handleCancel(timer.id, 'dismiss'));
      if (handle) activeAlarms.set(timer.id, handle);

      if (elapseSeam.fn) elapseSeam.fn(timer);
    }

    /**
     * Renders one shared pending badge per queued mutation that has no card of
     * its own. Uses the same `pendingBadgeFor` descriptor the inventory list
     * uses, so the indication is identical wherever it appears.
     */
    function renderPending() {
      while (pendingWrap.firstChild) pendingWrap.removeChild(pendingWrap.firstChild);
      for (const [key, entry] of pendingOutbox) {
        if (entry.kind === 'control') continue; // badged on the card itself
        const bd = pendingBadgeFor(key);
        const badge = document.createElement('span');
        badge.className = bd.className;
        badge.dataset.role = bd.dataRole;
        badge.dataset.outboxKey = key;
        badge.textContent = t(bd.i18nKey);
        pendingWrap.append(badge);
      }
    }

    /**
     * Overlay the queued-offline state onto a freshly fetched server list, so a
     * poll never resurrects a row whose cancel is still queued nor drops the
     * pending badge on a row whose control action is still queued.
     *
     * @param {Array<object>} serverTimers
     */
    function applyServerTimers(serverTimers) {
      let next = serverTimers;
      const cancelledIds = new Set();
      for (const entry of pendingOutbox.values()) {
        if (entry.kind === 'cancel' && entry.id) cancelledIds.add(entry.id);
      }
      if (cancelledIds.size > 0) next = next.filter((tm) => !cancelledIds.has(tm.id));
      for (const [key, entry] of pendingOutbox) {
        if (entry.kind === 'control' && entry.id) {
          next = applyOptimistic(next, { type: 'patch', id: entry.id, key, patch: {} });
        }
      }
      state.timers = next;
    }

    function renderList() {
      renderPending();
      cardRefs.clear();
      while (listWrap.firstChild) listWrap.removeChild(listWrap.firstChild);

      // A failed load is not an empty list. Rendering the empty state on error
      // made a server failure indistinguishable from "you have no timers", so
      // the natural response was to create a duplicate.
      if (state.loadError) {
        listWrap.append(stateError({ onRetry: () => load() }));
        return;
      }

      if (state.timers.length === 0) {
        listWrap.append(stateEmpty({
          titleKey: 'timers.empty_title',
          subtitleKey: 'timers.empty_subtitle',
        }));
        return;
      }

      for (const timer of state.timers) {
        const { el, remainingEl } = timerCard(
          timer,
          { onControl: handleControl, onCancel: handleCancel },
          formatDuration(deriveDisplayMs(timer)),
        );
        cardRefs.set(timer.id, { remainingEl });
        listWrap.append(el);
      }
    }

    function updateCountdownDisplays(nowMs) {
      for (const timer of state.timers) {
        if (timer.status !== 'running' && timer.status !== 'paused') continue;
        const ref = cardRefs.get(timer.id);
        if (!ref) continue;
        ref.remainingEl.textContent = formatDuration(deriveDisplayMs(timer, nowMs));
      }
    }

    async function handleControl(id, action) {
      try {
        const updated = await apiPatch(`/timers/${id}`, { action });
        // Offline, the outbox answers with a {queued:true, key} envelope, not a
        // timer. Feeding that to the reducer appended a row with no id — a
        // ghost timer that could not be controlled or dismissed. Instead the
        // existing row carries the shared pending badge until the SW resolves
        // the entry (IG-R10).
        if (updated && updated.queued === true) {
          const key = updated.key || `pending-control-${id}`;
          pendingOutbox.set(key, { kind: 'control', id, snapshot: null });
          state.timers = applyOptimistic(state.timers, { type: 'patch', id, key, patch: {} });
          renderList();
          toast.show(t('inventory.pending_badge'), 'info');
          return;
        }
        state.timers = applyTimerUpdate(state.timers, updated);
        pruneAlarmedIds();
        renderList();
        const toastKey = CONTROL_TOAST_KEY[action];
        if (toastKey) toast.show(t(toastKey), 'success');
      } catch (err) {
        const isNetwork = err && err.code === 'network';
        toast.show(isNetwork ? t('errors.network') : t('errors.server'), 'error');
      }
    }

    /**
     * @param {string} id
     * @param {'cancel'|'dismiss'} intent Which button was pressed — the
     *   confirmation is that button's past tense (IG-R5).
     */
    async function handleCancel(id, intent = 'cancel') {
      const saved = state.timers.find((t2) => t2.id === id);
      state.timers = removeTimerById(state.timers, id);
      alarmedIds.delete(id);
      forgetElapsePatch(id);
      stopAlarm(id);
      renderList();

      try {
        const result = await apiDel(`/timers/${id}`);
        if (result && result.queued === true) {
          // The row is already gone locally, so the pending badge goes on the
          // page-level strip; the snapshot restores it if the replay conflicts
          // or expires.
          const key = result.key || `pending-cancel-${id}`;
          pendingOutbox.set(key, { kind: 'cancel', id, snapshot: saved || null });
          renderList();
          toast.show(t('inventory.pending_badge'), 'info');
          return;
        }
        toast.show(t(intent === 'dismiss' ? 'timers.toast_dismissed' : 'timers.toast_cancelled'), 'success');
      } catch (err) {
        const isNetwork = err && err.code === 'network';
        if (!isNetwork && saved) {
          state.timers = [...state.timers, saved];
          renderList();
        }
        toast.show(isNetwork ? t('errors.network') : t('errors.server'), 'error');
      }
    }

    function handleCreated(created) {
      state.timers = [...state.timers, created];
      renderList();
    }

    /** @param {string} key Outbox key for a create that was queued offline. */
    function handleCreateQueued(key) {
      pendingOutbox.set(key, { kind: 'create', id: null, snapshot: null });
      renderPending();
    }

    createFormMount.append(
      timerCreateForm({ onCreated: handleCreated, onQueued: handleCreateQueued }).el,
    );

    // ---------------------------------------------------------------------
    // Initial load
    // ---------------------------------------------------------------------

    async function load() {
      const skeleton = stateLoading({ rows: 2 });
      listWrap.append(skeleton);
      try {
        const result = await apiGet('/timers');
        applyServerTimers((result && Array.isArray(result.timers)) ? result.timers : []);
        state.loadError = false;
      } catch {
        // Keep whatever was last known good and surface the failure as a
        // failure — never fall through to the empty branch.
        state.loadError = true;
      }
      skeleton.remove();
      renderList();
    }

    // ---------------------------------------------------------------------
    // Poll refresh — silent background fetch, no skeleton. Mirrors
    // pages/shopping.js exactly (pause-when-hidden, immediate-on-focus).
    // ---------------------------------------------------------------------

    async function pollRefresh() {
      if (document.visibilityState === 'hidden') return;
      try {
        const result = await apiGet('/timers');
        applyServerTimers((result && Array.isArray(result.timers)) ? result.timers : []);
        // Recovery path: without this the page stays on the error card forever
        // once a load has failed, because renderList() checks loadError first.
        // A successful poll IS the connection coming back.
        state.loadError = false;
        pruneAlarmedIds();
        renderList();
      } catch {
        // Silent failure.
      }
    }

    // ---------------------------------------------------------------------
    // 1 Hz local tick — recomputes each running/paused timer's display from
    // ends_at/remaining_ms (never from a server-ticked value; design D1) and
    // fires the alarm seam the instant a running timer's ends_at is reached.
    // ---------------------------------------------------------------------

    function tick() {
      const now = Date.now();
      updateCountdownDisplays(now);
      for (const elapsedTimer of findElapsedTimers(state.timers, now)) {
        onElapseDetected(elapsedTimer);
      }
    }

    // ---------------------------------------------------------------------
    // visibilitychange: recompute from ends_at immediately (firing the alarm
    // for anything that elapsed while hidden — spec "Elapsed-while-hidden
    // fires on return"), then run the same immediate poll refresh as
    // pages/shopping.js.
    // ---------------------------------------------------------------------

    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        tick();
        pollRefresh();
      }
    }

    function onWindowFocus() {
      pollRefresh();
    }

    const pollTimer = setInterval(pollRefresh, POLL_INTERVAL_MS);
    if (pollTimer && typeof pollTimer.unref === 'function') pollTimer.unref();

    const tickTimer = setInterval(tick, TICK_INTERVAL_MS);
    if (tickTimer && typeof tickTimer.unref === 'function') tickTimer.unref();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onWindowFocus);

    await load();

    // ---------------------------------------------------------------------
    // Outbox wiring (IG-R10) — same shape as pages/inventory.js:
    //   OUTBOX_CONFIRMED {key} → drop the overlay, refetch canonical state
    //   OUTBOX_CONFLICT  {key} → drop the overlay, restore what was cancelled
    //   OUTBOX_EXPIRED   {key} → as conflict, with the expiry copy
    // ---------------------------------------------------------------------

    function handleSwMessage(event) {
      const data = event && event.data;
      if (!data || typeof data.type !== 'string') return;

      const { type, key } = data;
      if (!pendingOutbox.has(key)) return;
      const entry = pendingOutbox.get(key);

      if (type === 'OUTBOX_CONFIRMED') {
        pendingOutbox.delete(key);
        state.timers = reconcile(state.timers, { type: 'confirm', key });
        renderList();
        pollRefresh();
        return;
      }

      if (type === 'OUTBOX_CONFLICT' || type === 'OUTBOX_EXPIRED') {
        pendingOutbox.delete(key);
        // 'expired' clears the optimistic flags without leaving a _conflict
        // marker — the row is restored (below) or refetched, so there is no
        // half-applied local state left for the user to reason about.
        state.timers = reconcile(state.timers, { type: 'expired', key });
        if (entry.kind === 'cancel' && entry.snapshot) {
          state.timers = applyTimerUpdate(state.timers, entry.snapshot);
        }
        renderList();
        toast.show(
          t(type === 'OUTBOX_CONFLICT' ? 'inventory.conflict_toast' : 'inventory.outbox_expired'),
          'warning',
        );
      }
    }

    const swContainer = typeof navigator !== 'undefined' && navigator.serviceWorker;
    if (swContainer && typeof swContainer.addEventListener === 'function') {
      swContainer.addEventListener('message', handleSwMessage);
    }

    // On reconnect, ask the active SW to replay the outbox with a fresh token
    // (the SW never stores the JWT — the page supplies it per replay).
    function onOnline() {
      const sw = typeof navigator !== 'undefined' && navigator.serviceWorker;
      if (!sw || !sw.controller) return;
      sw.controller.postMessage({ type: 'REPLAY_OUTBOX', token: getToken() || '' });
    }

    window.addEventListener('online', onOnline);

    return function cleanup() {
      clearInterval(pollTimer);
      clearInterval(tickTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('online', onOnline);
      if (swContainer && typeof swContainer.removeEventListener === 'function') {
        swContainer.removeEventListener('message', handleSwMessage);
      }
      // Stop every still-ringing alarm so nothing survives navigation — a
      // ringing tone's setInterval + AudioContext are otherwise left running
      // module-scoped forever (the runaway-alarm leak this fix closes).
      for (const id of Array.from(activeAlarms.keys())) stopAlarm(id);
    };
  },
};
